import React, { useEffect, useState } from 'react';
import { Navbar, Container, Nav, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { signOut, getCurrentUser } from '@aws-amplify/auth';
import styles from './Header.module.css';
import SignupPopup from './SignupPopup';
import LoginPopup from './LoginPopup';
import MainMenu from './MainMenu';
import { getSubscription, SubscriptionData } from '../utils/subscription';
import { applyThemeBasedOnAuth } from '../utils/theme';

/*https://discord.gg/EQm6ZNMPh5*/

const Header: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionVariant, setSubscriptionVariant] = useState<SubscriptionData['variant_name']>(null);
  const navigate = useNavigate();

  const resetVideoQualityIfNeeded = () => {
    const currentVideoQuality = localStorage.getItem('video_quality');
    if (currentVideoQuality === '1440p' || currentVideoQuality === '4k') {
      localStorage.setItem('video_quality', '1080p');
    }
  };
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
        const subscription = await getSubscription();
        setHasSubscription(subscription.is_active);
        setSubscriptionVariant(subscription.variant_name ?? null);
        applyThemeBasedOnAuth(true, subscription.is_active);
        if (!subscription.is_active) {
          resetVideoQualityIfNeeded();
        }
      } catch {
        setIsAuthenticated(false);
        setHasSubscription(false);
        setSubscriptionVariant(null);
        applyThemeBasedOnAuth(false, false);
        resetVideoQualityIfNeeded();
      } finally {
        setIsAuthChecked(true);
      }
    };

    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut({global: true});
      setIsAuthenticated(false);
      setHasSubscription(false);
      setSubscriptionVariant(null);
      applyThemeBasedOnAuth(false, false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <Navbar className={styles.header}>
        <Container fluid>
          <Navbar.Brand as={Link} to="/" className={styles.brand}>
            <div className={styles.logoWrapper}>
              <img
                src="/valolens-logo-transparent-192.png"
                width="30"
                height="30"
                className={styles.logo}
                alt="Valolens logo"
              />
              <span className={styles.logoLabel}>Valolens</span>
              {hasSubscription && (
                subscriptionVariant === 'team' ? (
                  <span className={styles.teamLabel}>TEAM</span>
                ) : (
                  <span className={styles.proLabel}>PRO</span>
                )
              )}
            </div>
          </Navbar.Brand>
          <Nav className="ml-auto">
            {isAuthChecked && (
              isAuthenticated ? (
                <MainMenu 
                  onLogout={handleLogout} 
                  hasSubscription={hasSubscription}
                  subscriptionVariant={subscriptionVariant}
                />
              ) : (
                <>
                  <Button variant="secondary" size="sm" className="me-2" onClick={() => setShowSignup(true)}>
                    <i className="bi bi-person me-2"></i>
                    Sign Up
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowLogin(true)}>
                    <i className="bi bi-door-open me-2"></i>
                    Login
                  </Button>
                </>
              )
            )}
          </Nav>
        </Container>
      </Navbar>

      <SignupPopup
        show={showSignup}
        onHide={() => setShowSignup(false)}
        onLogin={async () => {
          setIsAuthenticated(true);
          const subscription = await getSubscription();
          setHasSubscription(subscription.is_active);
          setSubscriptionVariant(subscription.variant_name ?? null);
          applyThemeBasedOnAuth(true, subscription.is_active);
        }}
      />

      <LoginPopup
        show={showLogin}
        onHide={() => setShowLogin(false)}
        onLogin={async () => {
          setIsAuthenticated(true);
          const subscription = await getSubscription();
          setHasSubscription(subscription.is_active);
          setSubscriptionVariant(subscription.variant_name ?? null);
          applyThemeBasedOnAuth(true, subscription.is_active);
          window.location.reload();
        }}
      />
    </>
  );
};

export default Header;
